/**
 * Reorderable member-info columns (the icon-text rows in the first planner column).
 *
 * The picker lets users drag these into any order and that order drives the member
 * rows. Custom attributes are dynamic, so a column key is either one of these fixed
 * keys or `customAttr_<attributeId>`. "Billable capacity" is deliberately NOT here —
 * it renders as a pinned progress bar at the bottom of the card, not a draggable row.
 */
export const REORDERABLE_USER_INFO_COLUMN_KEYS = [
  'isManager',
  'memberManager',
  'department',
  'role',
  'seniority',
  'location',
  'capacityPerDay',
  'membershipType',
] as const;

export type FixedUserInfoColumnKey = (typeof REORDERABLE_USER_INFO_COLUMN_KEYS)[number];

/** A reorderable column key: a fixed key or a custom-attribute key (`customAttr_<id>`). */
export type UserInfoColumnKey = string;

const CUSTOM_ATTRIBUTE_COLUMN_PREFIX = 'customAttr_';

export const buildCustomAttributeColumnKey = (attributeId: number): UserInfoColumnKey =>
  `${CUSTOM_ATTRIBUTE_COLUMN_PREFIX}${attributeId}`;

export const isCustomAttributeColumnKey = (key: UserInfoColumnKey): boolean =>
  key.startsWith(CUSTOM_ATTRIBUTE_COLUMN_PREFIX);

/** Parses the attribute id out of a `customAttr_<id>` key, or null for fixed keys. */
export const parseCustomAttributeColumnKey = (key: UserInfoColumnKey): number | null => {
  if (!isCustomAttributeColumnKey(key)) {
    return null;
  }
  const id = Number(key.slice(CUSTOM_ATTRIBUTE_COLUMN_PREFIX.length));
  return Number.isNaN(id) ? null : id;
};

/**
 * Reconciles a persisted order with the currently-available column keys:
 * keeps the saved order for keys that still exist, drops keys that are gone
 * (e.g. a deleted custom attribute), and appends newly-available keys at the end
 * in their canonical order (fixed keys first, then custom attributes).
 */
export const reconcileUserInfoOrder = (
  persistedOrder: UserInfoColumnKey[],
  availableCustomAttributeIds: number[]
): UserInfoColumnKey[] => {
  const availableKeys = [
    ...REORDERABLE_USER_INFO_COLUMN_KEYS,
    ...availableCustomAttributeIds.map(buildCustomAttributeColumnKey),
  ];
  const availableSet = new Set(availableKeys);

  const kept = persistedOrder.filter(key => availableSet.has(key));
  const keptSet = new Set(kept);
  const appended = availableKeys.filter(key => !keptSet.has(key));

  return [...kept, ...appended];
};
